import React, { FC } from 'react';
import { Dialog, Portal, CloseButton, Spinner, Box } from '@chakra-ui/react';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import {
  Plus,
  X,
  Info,
} from '@phosphor-icons/react';
import { IWorkout } from '../../../models/programming';
import { WORKOUT_TYPE, WORKOUT_CATEGORY, getDefaultLoadUnit } from '../../../enums/programming';
import styles from './WorkoutBuilderModal.module.scss';
import { useWorkoutBuilder } from './useWorkoutBuilder';
import { OccurrenceMode } from './workoutBuilderTypes';
import MetadataSection from './MetadataSection';
import BlockExerciseList from './BlockExerciseList';
import HelpDrawer from './HelpDrawer';
import ExercisePickerModal from './ExercisePickerModal';
import Typhography from '../../../shared/Typhography/Typhography';
import CustomButton from '../../../shared/CustomButton/CustomButton';

// --- Main component ---

interface WorkoutBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  workout: IWorkout | null;
  occurrenceMode?: OccurrenceMode | null;
  onSaved?: (workout: IWorkout) => void;
  initialCategory?: WORKOUT_CATEGORY;
  /** Atleta selezionato: abilita la preview %→assoluto degli step endurance (BUG E). */
  previewAthleteId?: string;
}

const WorkoutBuilderModal: FC<WorkoutBuilderModalProps> = ({
  isOpen,
  onClose,
  workout,
  occurrenceMode,
  onSaved,
  initialCategory,
  previewAthleteId,
}) => {
  const {
    title, setTitle,
    workoutType, setWorkoutType,
    tags, setTags,
    athleteNotes, setAthleteNotes,
    isTemplate, setIsTemplate,
    workoutCategory, setWorkoutCategory,
    activeDay, setActiveDay,
    days,
    newBlockType, setNewBlockType,
    exercisePickerTarget, setExercisePickerTarget,
    selectedExercises,
    expandedExercises, setExpandedExercises,
    toggleExpanded,
    activeExercise,
    overExerciseId,
    showHelp, setShowHelp,
    addDay, removeDay, updateDayTitle,
    addBlock, addBlockFromTemplate, removeBlock, updateBlock,
    addExercise, removeExercise, updateExercise,
    createSuperset, removeFromSuperset, updateSupersetSets,
    toggleExerciseSelection,
    handleDragStart, handleDragOver, handleDragEnd,
    estimatedDuration, isEdit, canSave, saveDisabledReason, currentDay, saveMutation,
    isNonStandardMethod, isLoadingWorkout,
  } = useWorkoutBuilder({ workout, isOpen, onClose, occurrenceMode, onSaved, initialCategory });

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      size="xl"
      open={isOpen}
      placement="center"
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className={styles.modalContent}>
            {/* Header */}
            <div className={styles.modalHeader}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Typhography size="bold-20">
                    {isEdit ? 'Modifica scheda' : 'Nuova scheda'}
                  </Typhography>
                  <CustomButton
                    color="text"
                    onClick={() => setShowHelp(true)}
                    className={styles.infoBtn}
                    disabled={false}
                  >
                    <Info size={13} weight="bold" />
                  </CustomButton>
                </div>
                <Typhography size="regular-14" className="color-text-2">
                  {isEdit ? 'Modifica i blocchi, gli esercizi e i parametri' : 'Configura giorni, blocchi ed esercizi'}
                </Typhography>
              </div>
              <Dialog.CloseTrigger asChild disabled={saveMutation.isPending}>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </div>

            {/* Body */}
            <div className={styles.modalBody}>
              {isLoadingWorkout ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
                  <LeopartLoader />
                </div>
              ) : (
              <>
              {/* Occurrence scope banner */}
              {occurrenceMode && (
                <Box
                  px={4} py={2} mb={3} borderRadius="md"
                  bg={
                    occurrenceMode.scope === 'single' ? 'rgba(6, 52, 68, 0.06)'
                    : occurrenceMode.scope === 'this_and_future' ? 'rgba(245, 179, 80, 0.15)'
                    : 'rgba(6, 52, 68, 0.1)'
                  }
                >
                  <Typhography size="bold-12">
                    {occurrenceMode.scope === 'single' && 'Stai modificando solo questa occorrenza'}
                    {occurrenceMode.scope === 'this_and_future' && 'Stai modificando questa e le occorrenze future'}
                    {occurrenceMode.scope === 'all' && 'Stai modificando tutte le occorrenze non completate'}
                  </Typhography>
                </Box>
              )}

              {/* Section 1: metadata */}
              <MetadataSection
                title={title}
                setTitle={setTitle}
                workoutType={workoutType}
                setWorkoutType={setWorkoutType}
                workoutCategory={workoutCategory}
                setWorkoutCategory={setWorkoutCategory}
                estimatedDuration={estimatedDuration}
                tags={tags}
                setTags={setTags}
                athleteNotes={athleteNotes}
                setAthleteNotes={setAthleteNotes}
                disableTypeChange={!!occurrenceMode}
              />

              {/* Section 2: Day tabs */}
              <div className={styles.builderSection}>
                {workoutType === WORKOUT_TYPE.MULTI_DAY && (
                  <div className={styles.tabBar}>
                    {days.map((day, idx) => (
                      <button
                        key={day._localId}
                        type="button"
                        className={`${styles.dayTab} ${
                          activeDay === idx ? styles.dayTabActive : ''
                        }`}
                        onClick={() => setActiveDay(idx)}
                      >
                        {day.title.trim() || `Giorno ${idx + 1}`}
                        {days.length > 1 && (
                          <span
                            className={styles.dayTabClose}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeDay(idx);
                            }}
                            title="Rimuovi giorno"
                          >
                            <X size={12} weight="bold" />
                          </span>
                        )}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={styles.addDayBtn}
                      onClick={addDay}
                      title="Aggiungi giorno"
                    >
                      <Plus size={14} weight="bold" />
                      Aggiungi giorno
                    </button>
                  </div>
                )}

                {/* Day content */}
                {currentDay && (
                  <div className={styles.dayContent}>
                    {/* Day custom title (multi day only) */}
                    {workoutType === WORKOUT_TYPE.MULTI_DAY && (
                      <div className={styles.dayTitleRow}>
                        <input
                          type="text"
                          className={styles.dayTitleInput}
                          placeholder={`Titolo giorno ${activeDay + 1} (opzionale)`}
                          value={currentDay.title}
                          onChange={(e) =>
                            updateDayTitle(currentDay._localId, e.target.value)
                          }
                        />
                      </div>
                    )}

                    {/* Blocks list */}
                    <BlockExerciseList
                      previewAthleteId={previewAthleteId}
                      currentDay={currentDay}
                      dayLocalId={currentDay._localId}
                      blocks={currentDay.blocks}
                      addBlock={addBlock}
                      addBlockFromTemplate={addBlockFromTemplate}
                      removeBlock={removeBlock}
                      updateBlock={updateBlock}
                      addExercise={addExercise}
                      removeExercise={removeExercise}
                      updateExercise={updateExercise}
                      createSuperset={createSuperset}
                      removeFromSuperset={removeFromSuperset}
                      updateSupersetSets={updateSupersetSets}
                      toggleExerciseSelection={toggleExerciseSelection}
                      toggleExpanded={toggleExpanded}
                      setExpandedExercises={setExpandedExercises}
                      handleDragStart={handleDragStart}
                      handleDragOver={handleDragOver}
                      handleDragEnd={handleDragEnd}
                      selectedExercises={selectedExercises}
                      expandedExercises={expandedExercises}
                      activeExercise={activeExercise}
                      overExerciseId={overExerciseId}
                      exercisePickerTarget={exercisePickerTarget}
                      setExercisePickerTarget={setExercisePickerTarget}
                      newBlockType={newBlockType}
                      setNewBlockType={setNewBlockType}
                      isNonStandardMethod={isNonStandardMethod}
                    />
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            {/* Footer */}
            <div className={styles.modalFooter}>
              {!occurrenceMode && (
                <label className={styles.templateCheckbox}>
                  <input
                    type="checkbox"
                    checked={isTemplate}
                    onChange={(e) => setIsTemplate(e.target.checked)}
                  />
                  <Typhography size="regular-14">Salva come template</Typhography>
                </label>
              )}
              <div className={styles.footerActions}>
                {saveDisabledReason && (
                  <Typhography size="regular-12" className={styles.disabledHint}>
                    {saveDisabledReason}
                  </Typhography>
                )}
                <CustomButton
                  color="outline"
                  onClick={onClose}
                  disabled={saveMutation.isPending}
                >
                  Annulla
                </CustomButton>
                <CustomButton
                  color="secondary"
                  onClick={() => saveMutation.mutate()}
                  disabled={!canSave || saveMutation.isPending}
                >
                  {saveMutation.isPending ? <Spinner size="sm" /> : (isEdit ? 'Salva modifiche' : 'Crea scheda')}
                </CustomButton>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>

      {/* Help Drawer */}
      <HelpDrawer isOpen={showHelp} onClose={() => setShowHelp(false)} />

      {/* Exercise Picker Modal */}
      <ExercisePickerModal
        target={exercisePickerTarget}
        onSelect={(target, exercise) => {
          const inputMode = exercise.defaultInputMode;
          updateExercise(
            target.dayLocalId,
            target.blockLocalId,
            target.exLocalId,
            {
              exerciseId: exercise._id,
              exerciseName: exercise.name.it || exercise.name.en || '',
              searchQuery: exercise.name.it || exercise.name.en || '',
              defaultInputMode: inputMode,
              slug: (exercise as any).slug,
              // Reset unità di carico al default dell'input mode quando si
              // seleziona un nuovo esercizio (evita unità incoerenti).
              loads: [
                {
                  value: '',
                  unit: getDefaultLoadUnit(inputMode),
                },
              ],
            },
          );
        }}
        onClose={() => setExercisePickerTarget(null)}
      />
    </Dialog.Root>
  );
};

export default WorkoutBuilderModal;
